/**
 * LegendModal Component
 * 
 * Displays educational terms and acronyms glossary to help parents
 * understand school terminology and app-specific terms.
 * 
 * @module LegendModal
 */

import React, { memo } from 'react';
import PropTypes from 'prop-types';

/**
 * LegendModal - Glossary of educational terms and acronyms
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Handler to close the modal
 * @param {Function} props.playSound - Function to play sound effects
 * @param {Function} props.triggerHaptic - Function to trigger haptic feedback
 * 
 * @returns {JSX.Element} LegendModal component
 */
const LegendModal = memo(({
  isOpen,
  onClose,
  playSound,
  triggerHaptic
}) => {
  if (!isOpen) return null;

  const handleClose = () => {
    playSound('click');
    triggerHaptic('light');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 md:p-8">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">📖</div>
            <h2 className="text-2xl md:text-3xl font-bold text-purple-600 mb-2">Educational Terms & Acronyms</h2>
            <p className="text-gray-600">A guide to help parents understand school terminology</p>
          </div>
          <div className="space-y-6">
            {/* Standards-Based Grading */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200">
              <h3 className="text-xl font-bold text-green-700 mb-4">📊 Standards-Based Grading</h3>
              <div className="mb-4 bg-white p-4 rounded-lg shadow-sm">
                <p className="text-sm text-gray-700 mb-3">
                  <strong>What is Standards-Based Grading?</strong><br/>
                  A system where students receive feedback around "fixed targets" or competencies. 
                  Non-academic factors (participation, late assignments, etc.) are removed, so the final grade 
                  represents what the student knows and can do.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-blue-500">
                  <span className="font-bold text-blue-600">PF - Proficient:</span>
                  <p className="text-sm text-gray-600">"I got it! I have mastered the content!"</p>
                </div>
                <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-orange-500">
                  <span className="font-bold text-orange-600">PG - Progressing:</span>
                  <p className="text-sm text-gray-600">"I am almost there. I need some support with the skill."</p>
                </div>
                <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-red-500">
                  <span className="font-bold text-red-600">DV - Developing:</span>
                  <p className="text-sm text-gray-600">"I need additional class and home support. Urgent intervention is needed."</p>
                </div>
              </div>
              <div className="mt-4 bg-white p-4 rounded-lg shadow-sm">
                <h4 className="font-bold text-green-700 mb-2">Key Benefits:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Learning is prioritized over compliance</li>
                  <li>• Growth mindset develops</li>
                  <li>• Motivation to learn increases</li>
                  <li>• Grades have meaning</li>
                  <li>• Clear vision of success</li>
                </ul>
              </div>
            </div>

            {/* First Grade Specific Terms */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border-2 border-blue-200">
              <h3 className="text-xl font-bold text-blue-700 mb-4">🎒 First Grade Terms</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <span className="font-bold text-blue-600">ELA:</span>
                    <p className="text-sm text-gray-600">English Language Arts</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <span className="font-bold text-blue-600">CVC:</span>
                    <p className="text-sm text-gray-600">Consonant-Vowel-Consonant words</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <span className="font-bold text-blue-600">Sight Words:</span>
                    <p className="text-sm text-gray-600">Common words learned by sight</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <span className="font-bold text-blue-600">Phonics:</span>
                    <p className="text-sm text-gray-600">Letter sounds and blending</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <span className="font-bold text-blue-600">Number Sense:</span>
                    <p className="text-sm text-gray-600">Understanding numbers 0-20</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <span className="font-bold text-blue-600">Skip Counting:</span>
                    <p className="text-sm text-gray-600">Counting by 2s, 5s, 10s</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <span className="font-bold text-blue-600">Story Elements:</span>
                    <p className="text-sm text-gray-600">Characters, setting, problem, solution</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <span className="font-bold text-blue-600">PTA:</span>
                    <p className="text-sm text-gray-600">Parent-Teacher Association</p>
                  </div>
                </div>
              </div>
            </div>

            {/* App-Specific Terms */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 border-2 border-yellow-200">
              <h3 className="text-xl font-bold text-yellow-700 mb-4">📱 App-Specific Terms</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <span className="font-bold text-yellow-600">Perfect Score:</span>
                    <p className="text-sm text-gray-600">100% on any subject game</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <span className="font-bold text-yellow-600">Confidence Level:</span>
                    <p className="text-sm text-gray-600">How confident Emmy feels about each subject</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <span className="font-bold text-yellow-600">Learning Streak:</span>
                    <p className="text-sm text-gray-600">Consecutive days of learning</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <span className="font-bold text-yellow-600">Achievement:</span>
                    <p className="text-sm text-gray-600">Special rewards earned for milestones</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <span className="font-bold text-yellow-600">Smart Shuffle:</span>
                    <p className="text-sm text-gray-600">Algorithm that reduces repeat questions</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <span className="font-bold text-yellow-600">Question History:</span>
                    <p className="text-sm text-gray-600">Tracks previously asked questions</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <button
              onClick={handleClose}
              className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-3 rounded-full font-bold text-lg transition-colors transform hover:scale-105 active:scale-95"
            >
              Got It! Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

LegendModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  playSound: PropTypes.func.isRequired,
  triggerHaptic: PropTypes.func.isRequired
};

LegendModal.displayName = 'LegendModal';

export default LegendModal;

